import { format, parseISO } from "date-fns";
import React from "react";
import { isValidDate, limitDecimal, toDollar } from "../../helpers";
import {
  standardizeKey,
  standardizeObjectKeys,
} from "../../utils/helpers/standardizer.helper";

//! UPDATED
// Get all object keys from the first column object in data
export const oldFormatDataTableColumns = (data) => {
  return Object.entries(data).map((key) => ({
    columntitle: key[0],
    ...key[1],
  }));
};
export const formatDataTableColumns = (reportName, data) => {
  return Object.entries(data[reportName] || {}).map((key) => ({
    columnTitle: key[0],
    ...key[1],
  }));
};

//! UPDATED
// Get all object keys from the first column object in data
export const oldExtractRowKeys = (data) => {
  return Object.entries(data).map((entry) =>
    Object.keys(entry[1]).map((k) => ({ label: k, key: k, isHeader: false }))
  )[0];
};
// Get all object keys from the first column object in data

export const extractRowKeys = (reportName, data) => {
  return Object.entries(data[reportName] || {}).map((entry) =>
    Object.keys(entry[1]).map((k) => ({ label: k, key: k }))
  )[0];
};

//! UPDATED
export const getReportBenchmark = (reportDataRows) => {
  return reportDataRows.filter((item) =>
    standardizeKey(item?.columnTitle)?.includes("Benchmark")
  )[0];
};

export const getOldReportBenchmark = (reportData) =>
  reportData.filter((item) => item?.columnTitle.includes("Benchmark"))[0];

export const getProfit = (benchmark, value, formatType) => {
  if (isNaN(benchmark) || isNaN(value) || benchmark === value) {
    // Neutral
    return {
      type: "neutral",
      value: formatLabel(formatType, 0),
      raw: value,
    };
  } else if (benchmark > value) {
    // Loss
    return {
      type: "loss",
      value: formatLabel(formatType, benchmark - value, 2),
      raw: benchmark - value,
    };
  }

  // Gain
  return {
    type: "gain",
    value: formatLabel(formatType, value - benchmark, 2),
    raw: value - benchmark,
  };
};

export const getFormatType = ({
  formatCellDataBy,
  standardizedTableDataTypes,
  standardizedRowKey,
  standardizedRowLabel,
  standardizedColumnTitleKey,
}) => {
  if (formatCellDataBy === "row") {
    return (
      standardizedTableDataTypes[standardizedRowKey] ||
      standardizedTableDataTypes[standardizedRowLabel]
    );
  }

  return (
    standardizedTableDataTypes[standardizedColumnTitleKey] ||
    standardizedTableDataTypes[standardizedColumnTitleKey]?.[
      standardizedRowKey
    ] ||
    standardizedTableDataTypes[standardizedColumnTitleKey]?.[
      standardizedRowLabel
    ]
  );
};

// export const formatLabel = (type, val, precision) => {
//   if (typeof val === "undefined" || val === "-") return "\u2014";

//   precision = precision ?? 2;

//   switch (type) {
//     case "number":
//       if (isNaN(val)) return val;
//       return Intl.NumberFormat("en-US").format(val);
//     case "percent":
//       if (isNaN(parseInt(val))) return val;
//       return `${limitDecimal(val, precision)}%`;
//     case "currency":
//       if (isNaN(val)) return val;
//       return toDollar(val);
//     case "date":
//       return !isValidDate(val) ? val : format(parseISO(val), "MM/dd/yy");
//     case "title case":
//       return val
//         .toLowerCase()
//         .split(" ")
//         .map((string) => string[0].toUpperCase() + string.substring(1))
//         .join(" ");
//     case "integer":
//       return Intl.NumberFormat("en-US").format(val.toFixed(0));
//     default:
//       if (typeof val === "number") return limitDecimal(val, precision);
//       return val;
//   }
// };

export const formatLabel = (type, val, precision = 2) => {
  if (typeof val === "undefined") return "\u2014";

  switch (type) {
    case "number":
      if (isNaN(val)) return val;
      return Intl.NumberFormat("en-US").format(val);
    case "percent":
      if (isNaN(val)) return val;
      return `${(val * 100).toFixed(2)}%`;
    case "currency":
      if (isNaN(val)) return val;
      return toDollar(val);
    case "date":
      return !isValidDate(val) ? val : format(parseISO(val), "MM/dd/yyyy");
    case "title case":
      return val
        .toLowerCase()
        .split(" ")
        .map((string) => string[0].toUpperCase() + string.substring(1))
        .join(" ");
    case "percentSuffix":
      return `${val}%`;
    case "integer":
      return val.toFixed(0);
    default:
      if (typeof val === "number") return limitDecimal(val, precision);
      return val;
  }
};

// @TODO: Use the `getOutputType` function moving forward
export const getReportType = (data) =>
  data?.["Cover"]["Report Type"]?.toLowerCase();

// export const getOutputType = (data) => {
//   const sectionKey = "Cover";
//   return {
//     reportType: data?.[sectionKey]?.["Report Type"]?.toLowerCase(),
//     assetType: data?.[sectionKey]?.["Asset Type"]?.toLowerCase(),
//   };
// };

export const getOutputType = (data) => {
  const reportName = "Cover";
  return {
    reportType: data?.[reportName]?.["Report Type"]?.toLowerCase(),
    assetType: data?.[reportName]?.["Asset Type"]?.toLowerCase(),
  };
};

export const validateObjectWithoutTotal = (data) => {
  return data?.length > 0 && !Object.keys(data[0] || {})?.includes("Total");
};

export const insertTotalData = (data) => {
  return data.map((item) => {
    item.Total = Object.values(item)
      .filter((val) => !isNaN(val))
      .reduce((a, b) => a + b, 0);
    return item;
  });
};

export const generateFootnote = (footnotes, copy) => {
  const footeNoteNumbers = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
  return footnotes.map((footnote, index) => {
    return (
      <React.Fragment key={index}>
        <br />[{footeNoteNumbers[index]}] {copy[footnote]}
      </React.Fragment>
    );
  });
};

const defaultTableRowKeys = [
  {
    key: "Number of Securities",
    label: "Number of Securities",
  },
  {
    key: "Beta to Index",
    label: "Beta to Index",
  },
  {
    key: "Effective Duration (OAD)",
    label: "Effective Duration",
  },
  {
    key: "Option Adjusted Spread (OAS)",
    label: "Option Adjusted Spread (OAS)",
  },
  {
    key: "Option Adjusted Spread Duration (OASD)",
    label: "Option Adjusted Spread Duration (OASD)",
  },
  {
    key: "Duration Times Spread (DTS)",
    label: "Duration Times Spread (DTS)",
  },
  {
    key: "Time to Maturity",
    label: "Time to Maturity",
  },
  {
    key: "Coupon",
    label: "Coupon",
  },
  {
    key: "Yield to Maturity [3]",
    label: "Yield to Maturity[3]",
  },
  {
    key: "Yield to Worst [3]",
    label: "Yield to Worst[3]",
  },
  {
    key: "Credit Quality",
    label: "Credit Quality",
  },
  {
    key: "Price",
    label: "Price",
  },
  {
    key: "% Callable",
    label: "% Callable",
  },
  {
    key: "Coupon Income (Next 12 months) [4]",
    label: "Coupon Income (Next 12 months)[4]",
  },
  {
    key: "Liquidity Score (LQA)",
    label: "Liquidity Score (LQA)",
  },
  {
    key: "ESG Rating",
    label: "ESG Rating",
  },
  {
    key: "Ending Tracking Error",
    label: "Tracking Error",
  },
  {
    key: "Tracking Error to Benchmark",
    label: "Tracking Error to Benchmark",
  },
  {
    key: "% of Market Value in Stocks",
    label: "% of Market Value in Stocks",
  },
  {
    key: "Beta to Benchmark",
    label: "Beta to Benchmark",
  },
  {
    key: "Dividend Yield",
    label: "Dividend Yield",
  },
  {
    key: "Weight in Top Ten Holdings (%)",
    label: "Weight in Top Ten Holdings (%)",
  },
  {
    key: "Average Weighted Market Cap (Billions $)****",
    label: "Average Weighted Market Cap (Billions $)[4]",
  },
  {
    key: "Large Cap (%)****",
    label: "Large Cap (%)[4]",
  },
  {
    key: "Mid Cap (%)****",
    label: "Mid Cap (%)[4]",
  },
  {
    key: "Small Cap (%)****",
    label: "Small Cap (%)[4]",
  },
  {
    key: "Defensive Sectors (%)****",
    label: "Defensive Sectors (%)[4]",
  },
  {
    key: "Highly Cyclical Sectors (%)****",
    label: "Highly Cyclical Sectors (%)[4]",
  },
  {
    key: "Moderately Cyclical Sectors (%)****",
    label: "Moderately Cyclical Sectors (%)[4]",
  },
];
export const tableRowKeysForTransitionCorpAndMuni = standardizeObjectKeys({
  "Yield to Maturity [3]": {
    key: "Yield to Maturity [4]",
    label: "Yield to Maturity[4]",
  },
  "Yield to Worst [3]": {
    key: "Yield to Worst [4]",
    label: "Yield to Worst[4]",
  },
  "Coupon Income (Next 12 months) [4]": {
    key: "Coupon Income (Next 12 months) [5]",
    label: "Coupon Income (Next 12 months)[5]",
  },
});

const isContainedOnVisibleRows = (standardizedVisibleRows) => {
  return ({ key }) => standardizedVisibleRows.includes(standardizeKey(key));
};

export const getTableRowKeys = (visibleRows, reportType, assetType) => {
  if (!visibleRows.length) return defaultTableRowKeys;

  const filteredRows = defaultTableRowKeys.filter(
    isContainedOnVisibleRows(visibleRows.map(standardizeKey))
  );

  if (
    reportType === "transition" &&
    (assetType === "corp" || assetType === "muni")
  ) {
    return filteredRows.map(({ key, label }) => {
      const overrideMetadata =
        tableRowKeysForTransitionCorpAndMuni[standardizeKey(key)];
      return overrideMetadata || { key, label };
    });
  }
  return filteredRows;
};
